import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProblemType, SimlabErrorDto, SKIP_INSUFFICIENT_PERMISSIONS_REDIRECT } from '@simOn/common/http';
import { PublicLinkNotificationService } from '@simOn/common/notification';
import { PublicLinkService } from '@simOn/public-link';
import { SpaceFacade } from '@simOn/space';
import { API_URL } from '@simOn/utils/tokens';
import { catchError, mergeMap, Observable, take, throwError } from 'rxjs';

@Injectable()
export class ApartmentIdApiInterceptor implements HttpInterceptor {
  private readonly simlabApi = inject(API_URL);
  private readonly spaceFacade = inject(SpaceFacade);
  private readonly router = inject(Router);

  private readonly _publicLink = inject(PublicLinkService, { optional: true });
  private readonly _publicLinkNotification = inject(PublicLinkNotificationService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.spaceFacade.selectedApartmentId$.pipe(
      take(1),
      mergeMap((selectedApartmentId) => {
        if (!req.url.includes(this.simlabApi)) {
          return next.handle(req);
        }
        const apartmentId =
          this._publicLink?.apartmentId || req.headers.get('apartmentId') || selectedApartmentId || '';
        const newRequest = req.clone({
          setHeaders: {
            apartmentId
          }
        });
        return apartmentId ? next.handle(newRequest) : next.handle(req);
      }),
      catchError((err) => {
        const simlabError = err.error as SimlabErrorDto;
        // A revoked or deleted JWT public link: end the visit and show the "no longer valid" page.
        if (err?.status === 401 && this._publicLink?.isJwtSession()) {
          this._publicLink.endSession();
          this.router.navigate(['public-link']);
          return throwError(() => err);
        }
        if (
          simlabError &&
          simlabError.ErrorCode === ProblemType.InsufficientPermissions &&
          !req.context.get(SKIP_INSUFFICIENT_PERMISSIONS_REDIRECT)
        ) {
          // A JWT public link lacks the capability for this endpoint: never send the visitor to login. Only a user
          // action (non-GET) gets the toast; background GETs fail silently instead of stacking toasts.
          if (this._publicLink?.isJwtSession()) {
            if (req.method !== 'GET') this._publicLinkNotification.isDisabledInPublicLinkToast();
            return throwError(() => err);
          }
          // Stay in the documents context — its spaces list is the natural fallback there.
          this.router.navigate([this.router.url.startsWith('/documents') ? '/documents' : '/']);
        }
        return throwError(() => err);
      })
    );
  }
}
